import type { PayRunFieldError } from './payRunsApiTypes';

export interface CreateFailureContext {
  runType: 'standard' | 'adjustment';
  /** Label of the selected Pay Period; only a Standard run takes its EFT release date from it. */
  periodLabel?: string;
  /** The EFT release date the run was submitted with (the Pay Period's for Standard, the typed one for Adjustment). */
  eftReleaseDate?: string;
}

const FIELD_LABELS: Record<string, string> = {
  businessRecordId: 'Business record',
  payGroupId: 'Pay group',
  payPeriodId: 'Pay period',
  displayLabel: 'Display label',
  notes: 'Notes',
  paymentDate: 'Payment date',
  eftReleaseDate: 'EFT release date',
  adjustmentClassification: 'Adjustment type',
  reasons: 'Reasons',
  reportingPeriodKind: 'Reporting period',
};

const ASK_ADMIN = 'ask an administrator to move this pay period\'s EFT release date';

// The API reports every EFT release date problem as `invalid_value`, so the message text is the only way to tell them apart.
// An unrecognised message falls through to the generic "label: server message" line, so nothing the server says is lost.
const describeEftReleaseDate = (message: string, { runType, periodLabel, eftReleaseDate }: CreateFailureContext): string | undefined => {
  const date = eftReleaseDate ? ` (${eftReleaseDate})` : '';
  const subject = runType === 'standard' && periodLabel ? `The EFT release date for ${periodLabel}${date}` : `The EFT release date${date}`;
  const standard = runType === 'standard';
  if (/in the past/i.test(message))
    return standard
      ? `${subject} is in the past, so a Pay Run cannot be created for it. Choose a later pay period, or ${ASK_ADMIN} to a future business day.`
      : `${subject} is in the past. Choose today or a later business day.`;
  // "Public holiday evaluation is unavailable ..." is a server fault, not a holiday, so only the affirmative wording matches.
  if (/\bis a\b.*\bpublic holiday\b/i.test(message))
    return standard
      ? `${subject} is a public holiday for this Business Record. Choose another pay period, or ${ASK_ADMIN} to a business day.`
      : `${subject} is a public holiday for this Business Record. Choose a business day.`;
  if (/business day/i.test(message))
    return standard
      ? `${subject} falls on a weekend. Choose another pay period, or ${ASK_ADMIN} to a business day.`
      : `${subject} falls on a weekend. Choose a business day.`;
  if (/on or before payment date/i.test(message))
    return standard
      ? `${subject} is after the payment date. Choose another pay period, or ask an administrator to correct this pay period's dates.`
      : `${subject} is after the payment date. Choose a date on or before the payment date.`;
  return undefined;
};

const describeFieldError = (error: PayRunFieldError, context: CreateFailureContext): string => {
  const label = FIELD_LABELS[error.field] ?? error.field;
  if (error.field === 'eftReleaseDate') {
    const described = describeEftReleaseDate(error.message, context);
    if (described) return described;
  }
  return `${label}: ${error.message}`;
};

/** The lines shown in the "Pay Run not created" banner: one per field error, or the server message when there are none. */
export const describeCreateFailure = (message: string, fieldErrors: PayRunFieldError[], context: CreateFailureContext): string[] => {
  if (fieldErrors.length === 0) return [message];
  return [...new Set(fieldErrors.map((error) => describeFieldError(error, context)))];
};
